// src/layout/Footer.js
import React, { useState, useEffect } from 'react';
import Logo from '../common/Logo';
import { callApi } from '../api/api';

// Haversine formula to calculate distance in km
const getDistanceKm = (lat1, lng1, lat2, lng2) => {
    const R = 6371;
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLng = (lng2 - lng1) * Math.PI / 180;
    const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
        Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
        Math.sin(dLng/2) * Math.sin(dLng/2);
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
};

const DEFAULT_STORE = {
    name: 'Vimalaxmi Medical Store',
    address: 'Shop no. 737, Sector 15, Koperkhairane, Navi Mumbai - 400709',
    phone: '+919029122955'
};

const Footer = ({ showPage, userCoordinates }) => {
    const [nearestStore, setNearestStore] = useState(DEFAULT_STORE);

    useEffect(() => {
        const fetchAndFindNearest = async () => {
            try {
                const res = await callApi('/stores');
                if (res.success && res.stores && res.stores.length > 0) {
                    if (userCoordinates && userCoordinates.lat && userCoordinates.lng) {
                        // Find nearest store by GPS distance
                        let nearest = res.stores[0];
                        let minDist = Infinity;
                        res.stores.forEach(store => {
                            const dist = getDistanceKm(userCoordinates.lat, userCoordinates.lng, store.lat, store.lng);
                            if (dist < minDist) {
                                minDist = dist;
                                nearest = store;
                            }
                        });
                        setNearestStore(nearest);
                    } else {
                        // No coordinates, just use the first active store
                        setNearestStore(res.stores[0]);
                    }
                }
            } catch (e) {
                // Silently fallback to default store
            }
        };
        fetchAndFindNearest();
    }, [userCoordinates]);

    return (
        <footer className="hidden md:block bg-white text-slate-600 py-16 px-4 sm:px-6 mt-auto border-t border-slate-200">
            {/* Trust Signals Row */}
            <div className="container mx-auto grid grid-cols-1 md:grid-cols-3 gap-8 mb-12 pb-12 border-b border-slate-100 text-center">
                <div className="flex flex-col items-center">
                    <div className="w-16 h-16 bg-sky-50 rounded-full flex items-center justify-center text-sky-600 text-2xl mb-4">
                        <i className="fas fa-shield-alt"></i>
                    </div>
                    <h4 className="font-bold text-slate-800 text-lg mb-2">100% Genuine Medicines</h4>
                    <p className="text-sm text-slate-500 max-w-xs">Directly sourced from trusted manufacturers and distributors.</p>
                </div>
                <div className="flex flex-col items-center">
                    <div className="w-16 h-16 bg-sky-50 rounded-full flex items-center justify-center text-sky-600 text-2xl mb-4">
                        <i className="fas fa-shipping-fast"></i>
                    </div>
                    <h4 className="font-bold text-slate-800 text-lg mb-2">Fast Local Delivery</h4>
                    <p className="text-sm text-slate-500 max-w-xs">Get your medicines delivered securely to your doorstep.</p>
                </div>
                <div className="flex flex-col items-center">
                    <div className="w-16 h-16 bg-sky-50 rounded-full flex items-center justify-center text-sky-600 text-2xl mb-4">
                        <i className="fas fa-lock"></i>
                    </div>
                    <h4 className="font-bold text-slate-800 text-lg mb-2">Secure Payments</h4>
                    <p className="text-sm text-slate-500 max-w-xs">Your data is safe. We use industry-standard encryption.</p>
                </div>
            </div>

            <div className="container mx-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-8 text-center md:text-left">
                {/* Brand & Description */}
                <div className="lg:col-span-1">
                    <div className="flex justify-center md:justify-start cursor-pointer" onClick={() => showPage('landing')}>
                        <Logo className="mb-4" />
                    </div>
                    <p className="text-slate-500 leading-relaxed mb-6 text-sm mt-4">
                        Your trusted neighborhood pharmacy. Quality medicines, precise billing, and caring support.
                    </p>
                </div>

                {/* Company */}
                <div>
                    <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wider mb-5">Company</h3>
                    <ul className="space-y-3 text-sm">
                        <li><button type="button" onClick={() => showPage('about-us')} className="text-slate-500 hover:text-sky-600 transition-colors duration-300">About Us</button></li>
                        <li><button type="button" onClick={() => showPage('join-us')} className="text-slate-500 hover:text-sky-600 transition-colors duration-300">Join Us</button></li>
                        <li><button type="button" onClick={() => showPage('corporates')} className="text-slate-500 hover:text-sky-600 transition-colors duration-300">Corporates</button></li>
                        <li><button type="button" onClick={() => showPage('careers')} className="text-slate-500 hover:text-sky-600 transition-colors duration-300">Careers</button></li>
                        <li><button type="button" onClick={() => showPage('blog')} className="text-slate-500 hover:text-sky-600 transition-colors duration-300">Blog</button></li>
                    </ul>
                </div>

                {/* Our Services */}
                <div>
                    <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wider mb-5">Services</h3>
                    <ul className="space-y-3 text-sm">
                        <li><button type="button" onClick={() => showPage('products')} className="text-slate-500 hover:text-sky-600 transition-colors duration-300">Order Medicines</button></li>
                        <li><button type="button" onClick={() => showPage('labs')} className="text-slate-500 hover:text-sky-600 transition-colors duration-300">Book Lab Tests</button></li>
                        <li><button type="button" onClick={() => showPage('upload-prescription')} className="text-slate-500 hover:text-sky-600 transition-colors duration-300">Upload Prescription</button></li>
                        <li><button type="button" onClick={() => showPage('health-insurance')} className="text-slate-500 hover:text-sky-600 transition-colors duration-300">Health Insurance</button></li>
                        <li><button type="button" onClick={() => showPage('orders')} className="text-slate-500 hover:text-sky-600 transition-colors duration-300">My Orders</button></li>
                    </ul>
                </div>

                {/* Support */}
                <div>
                    <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wider mb-5">Support</h3>
                    <ul className="space-y-3 text-sm">
                        <li><button type="button" onClick={() => showPage('help-center')} className="text-slate-500 hover:text-sky-600 transition-colors duration-300">Help Center</button></li>
                        <li><button type="button" onClick={() => showPage('privacy-policy')} className="text-slate-500 hover:text-sky-600 transition-colors duration-300">Privacy Policy</button></li>
                        <li><button type="button" onClick={() => showPage('terms-conditions')} className="text-slate-500 hover:text-sky-600 transition-colors duration-300">Terms & Conditions</button></li>
                        <li><button type="button" onClick={() => showPage('return-policy')} className="text-slate-500 hover:text-sky-600 transition-colors duration-300">Return Policy</button></li>
                        <li><button type="button" onClick={() => showPage('help-center')} className="text-slate-500 hover:text-sky-600 transition-colors duration-300">Contact Us</button></li>
                    </ul>
                </div>

                {/* Contact & Social */}
                <div>
                    <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wider mb-5">Connect</h3>
                    <p className="text-sky-600 font-bold mb-1 text-sm flex items-center gap-1">
                        <i className="fas fa-store text-sky-500"></i> {nearestStore.name}
                    </p>
                    <p className="text-slate-500 leading-relaxed mb-3 text-sm">
                        {nearestStore.address}
                    </p>
                    <p className="text-slate-500 leading-relaxed text-sm mb-4">
                        Phone: <a href={`tel:${nearestStore.phone}`} className="hover:text-sky-600 font-bold text-slate-800 transition-colors">{nearestStore.phone?.replace('+91', '+91 ')}</a>
                    </p>
                    <div className="flex justify-center md:justify-start space-x-4 mt-2">
                        <button type="button" aria-label="Facebook" className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-slate-500 hover:bg-sky-100 hover:text-sky-600 transition-all"><i className="fab fa-facebook-f text-sm"></i></button>
                        <button type="button" aria-label="Twitter" className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-slate-500 hover:bg-sky-100 hover:text-sky-600 transition-all"><i className="fab fa-twitter text-sm"></i></button>
                        <button type="button" aria-label="Instagram" className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-slate-500 hover:bg-sky-100 hover:text-sky-600 transition-all"><i className="fab fa-instagram text-sm"></i></button>
                    </div>
                </div>
            </div>
            
            <div className="container mx-auto text-center mt-16 pt-8 border-t border-slate-100">
                <p className="text-slate-500 text-sm">&copy; {new Date().getFullYear()} QwickMed. All rights reserved.</p>
                <p className="mt-2 text-xs text-slate-400">QwickMed is a brand of Caretica World Private Limited.</p>
            </div>
        </footer>
    );
}

export default Footer;
